/**
 * hub-session.guard.js
 *
 * Guard global que valida a sessão do Hub para todas as rotas,
 * exceto as rotas públicas definidas em PUBLIC_PATHS.
 *
 * Rotas podem restringir por permissão ou role específica via meta:
 *   meta: { hubPermission: 'SATISFACTION_SURVEY_VIEW' }
 *   meta: { hubPermissions: ['SATISFACTION_SURVEY_VIEW', 'SATISFACTION_SURVEY_CREATE'] }
 *   meta: { hubRequireAllPermissions: true }
 *   meta: { hubRoles: ['ADMINISTRADOR'] }
 */

import { HubSessionService } from '@/services/hub-session.service';

const PUBLIC_PATHS = ['/error', '/access-denied'];

const ADMIN_ROLES = ['SYSTEM_ADMIN', 'ADMINISTRADOR'];

const FIRST_SESSION_WAIT_MS = 3000;
const REFRESH_SESSION_WAIT_MS = 600;

function normalizeValue(value) {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

function normalizeArray(values) {
  if (!Array.isArray(values)) return [];

  return values.map(normalizeValue).filter(Boolean);
}

function getNowInSeconds() {
  return Math.floor(Date.now() / 1000);
}

function getSessionPayload() {
  return HubSessionService.getSessionPayload?.() || {};
}

function isPayloadValid(payload) {
  if (!payload || typeof payload !== 'object') return false;

  if (!payload.exp) return true;

  return Number(payload.exp) > getNowInSeconds();
}

function getUserRoles(payload = getSessionPayload()) {
  return normalizeArray(
    payload?.roles || payload?.role || payload?.user?.roles || payload?.user?.role || []
  );
}

function getUserPermissions(payload = getSessionPayload()) {
  return normalizeArray(
    payload?.perms ||
      payload?.permissions ||
      payload?.authorities ||
      payload?.user?.perms ||
      payload?.user?.permissions ||
      []
  );
}

function hasAdminRole(payload = getSessionPayload()) {
  const roles = getUserRoles(payload);

  return ADMIN_ROLES.some((role) => roles.includes(normalizeValue(role)));
}

function hasPermission(permission, payload = getSessionPayload()) {
  if (hasAdminRole(payload)) return true;

  const normalizedPermission = normalizeValue(permission);

  if (!normalizedPermission) return false;

  const perms = getUserPermissions(payload);

  return perms.includes(normalizedPermission);
}

function hasAnyPermission(permissions = [], payload = getSessionPayload()) {
  if (hasAdminRole(payload)) return true;

  return normalizeArray(permissions).some((permission) => {
    return hasPermission(permission, payload);
  });
}

function hasAllPermissions(permissions = [], payload = getSessionPayload()) {
  if (hasAdminRole(payload)) return true;

  return normalizeArray(permissions).every((permission) => {
    return hasPermission(permission, payload);
  });
}

function hasAnyRole(requiredRoles = [], payload = getSessionPayload()) {
  if (hasAdminRole(payload)) return true;

  const userRoles = getUserRoles(payload);
  const normalizedRequiredRoles = normalizeArray(requiredRoles);

  return normalizedRequiredRoles.some((role) => userRoles.includes(role));
}

function waitForSession(timeoutMs = FIRST_SESSION_WAIT_MS) {
  const alreadyHasValidToken = HubSessionService.isTokenValid?.() === true;

  return new Promise((resolve) => {
    let resolved = false;

    const finish = (value) => {
      if (resolved) return;

      resolved = true;
      window.removeEventListener('message', listener);
      clearTimeout(timer);
      resolve(value);
    };

    const listener = (event) => {
      if (event.data?.type !== 'SET_ACCESS_TOKEN') return;

      HubSessionService.saveSession(event.data.message);

      const payload = getSessionPayload();

      console.debug('[HubSessionGuard] Token recebido do Hub:', {
        email: payload?.email,
        sub: payload?.sub,
        roles: payload?.roles,
        perms: payload?.perms,
        exp: payload?.exp,
      });

      finish(isPayloadValid(payload));
    };

    window.addEventListener('message', listener);

    const timer = setTimeout(
      () => {
        const payload = getSessionPayload();

        console.debug('[HubSessionGuard] Usando sessão já existente:', {
          alreadyHasValidToken,
          email: payload?.email,
          sub: payload?.sub,
          roles: payload?.roles,
          perms: payload?.perms,
          exp: payload?.exp,
        });

        finish(alreadyHasValidToken && isPayloadValid(payload));
      },
      alreadyHasValidToken ? REFRESH_SESSION_WAIT_MS : timeoutMs
    );
  });
}

function getRouteMeta(to) {
  return to.matched.reduce((meta, record) => {
    return {
      ...meta,
      ...(record.meta || {}),
    };
  }, {});
}

function evaluateAccess(to) {
  const payload = getSessionPayload();
  const meta = getRouteMeta(to);

  const requiredPerm = meta?.hubPermission;
  const requiredPerms = meta?.hubPermissions;
  const requireAllPermissions = meta?.hubRequireAllPermissions === true;
  const requiredRoles = meta?.hubRoles;

  const userPerms = getUserPermissions(payload);
  const userRoles = getUserRoles(payload);

  console.debug('[HubSessionGuard] Avaliando rota:', {
    path: to.path,
    name: to.name,
    requiredPerm,
    requiredPerms,
    requireAllPermissions,
    requiredRoles,
    email: payload?.email,
    sub: payload?.sub,
    userRoles,
    userPerms,
    isAdmin: hasAdminRole(payload),
    hasRequiredPerm: requiredPerm ? hasPermission(requiredPerm, payload) : null,
  });

  if (!isPayloadValid(payload)) {
    return {
      allowed: false,
      reason: 'Sessão expirada ou inválida. Faça login novamente.',
    };
  }

  if (hasAdminRole(payload)) {
    return {
      allowed: true,
      reason: '',
    };
  }

  if (requiredPerm && !hasPermission(requiredPerm, payload)) {
    return {
      allowed: false,
      reason: `Acesso negado. Permissão "${requiredPerm}" necessária.`,
    };
  }

  if (Array.isArray(requiredPerms) && requiredPerms.length) {
    const allowedByPermissions = requireAllPermissions
      ? hasAllPermissions(requiredPerms, payload)
      : hasAnyPermission(requiredPerms, payload);

    if (!allowedByPermissions) {
      return {
        allowed: false,
        reason: `Acesso negado. Permissão necessária: ${requiredPerms.join(', ')}.`,
      };
    }
  }

  if (Array.isArray(requiredRoles) && requiredRoles.length && !hasAnyRole(requiredRoles, payload)) {
    return {
      allowed: false,
      reason: `Acesso negado. Perfil necessário: ${requiredRoles.join(', ')}.`,
    };
  }

  return {
    allowed: true,
    reason: '',
  };
}

export async function hubSessionGuard(to, _from, next) {
  if (PUBLIC_PATHS.includes(to.path)) {
    return next();
  }

  const sessionFound = await waitForSession();

  if (!sessionFound) {
    console.warn('[HubSessionGuard] Sessão não encontrada ou inválida.');

    return next({
      path: '/access-denied',
      query: {
        e: encodeURIComponent('Sessão não encontrada. Faça login novamente.'),
        details: '1',
      },
    });
  }

  const { allowed, reason } = evaluateAccess(to);

  if (!allowed) {
    console.warn('[HubSessionGuard] Acesso negado:', reason);

    return next({
      path: '/access-denied',
      query: {
        e: encodeURIComponent(reason),
        details: '1',
      },
    });
  }

  return next();
}
